/**
 * useGlobeRenderer - Hook for Three.js-specific rendering state.
 *
 * Consumes the globe/map context and provides values optimized for
 * Three.js rendering, including camera configuration and look direction.
 */

import { useMemo } from 'react'
import { useGlobeMap } from './useGlobeMap'
import { GLOBE_CONFIG } from '../constants'
import { latLngToSpherical, sphericalToCartesian } from '../utils/projection'

/**
 * Camera configuration for Three.js.
 */
export interface CameraConfig {
  /** Camera position (always at origin for inside-hemisphere view) */
  position: readonly [number, number, number]
  /** Field of view in degrees */
  fov: number
  /** Near clipping plane */
  near: number
  /** Far clipping plane */
  far: number
}

/**
 * Hook for Three.js rendering state.
 *
 * Converts the renderer-agnostic globe state into Three.js-specific values.
 *
 * @example
 * ```tsx
 * function GlobeScene() {
 *   const { cameraConfig, lookDirection, curvature } = useGlobeRenderer()
 *
 *   return (
 *     <Canvas camera={cameraConfig}>
 *       <MorphingHemisphere curvature={curvature} />
 *       <CameraController lookDirection={lookDirection} />
 *     </Canvas>
 *   )
 * }
 * ```
 */
export function useGlobeRenderer() {
  const { center, fov, curvature, heading, visibleTiles, mode, animating } =
    useGlobeMap()

  // Camera configuration (position at origin, looking outward)
  const cameraConfig = useMemo<CameraConfig>(
    () => ({
      position: [0, 0, 0] as const,
      fov,
      near: GLOBE_CONFIG.NEAR_CLIP,
      far: GLOBE_CONFIG.FAR_CLIP,
    }),
    [fov]
  )

  // Convert lat/lng to cartesian direction vector for camera look-at
  const lookDirection = useMemo(() => {
    const spherical = latLngToSpherical(
      center.latitude,
      center.longitude,
      GLOBE_CONFIG.SPHERE_RADIUS
    )
    return sphericalToCartesian(spherical)
  }, [center.latitude, center.longitude])

  // Heading as radians for camera rotation
  const headingRadians = useMemo(
    () => (heading * Math.PI) / 180,
    [heading]
  )

  return {
    /** Camera configuration for Three.js Canvas */
    cameraConfig,
    /** Field of view in degrees (convenience accessor) */
    fov,
    /** Direction vector for camera to look at */
    lookDirection,
    /** Current curvature for morphing geometry */
    curvature,
    /** Heading in radians for camera rotation */
    headingRadians,
    /** Tiles to render */
    visibleTiles,
    /** Current view mode */
    mode,
    /** Whether animation is in progress */
    animating,
  }
}
